import { useEffect, useMemo, useState } from 'react'
import { Plus } from 'lucide-react'
import { ColDef } from 'ag-grid-community'
import Button from '@/components/ui/Button'
import DataGrid from '@/components/data/DataGrid'
import Tag from '@/components/ui/Tag'
import WorkspaceCreateModal from './components/WorkspaceCreateModal'
import WorkspaceEditPanel from './components/WorkspaceEditPanel'
import WorkspaceDetailPanel from './components/WorkspaceDetailPanel'
import WorkspaceMenuSettingsEditPanel from './components/WorkspaceMenuSettingsEditPanel'
import { useAppDispatch, useAppSelector } from '@/store/hooks'
import { fetchWorkspaces, clearError, createWorkspace, updateWorkspace } from '@/store/workspaceManagementSlice'
import { fetchWorkspaceById } from '@/store/workspaceSlice'
import type { WorkspaceResponse } from '@/api/generated/models/WorkspaceResponse'
import { useAlert } from '@/components/ui/Alert/exports'
import { handleApiError, ErrorMessages } from '@/utils/api-error-handler'

export default function WorkspaceManagementPage() {
  const dispatch = useAppDispatch()
  const { showError, showSuccess } = useAlert()
  
  // Local state
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false)
  const [isEditPanelOpen, setIsEditPanelOpen] = useState(false)
  const [editingWorkspace, setEditingWorkspace] = useState<WorkspaceResponse | null>(null)
  const [isDetailPanelOpen, setIsDetailPanelOpen] = useState(false)
  // DetailPanel은 id만으로 자체 fetch. 단, EditPanel/MenuSettingsEditPanel은 객체 prop 필요
  const [selectedWorkspaceForDetail, setSelectedWorkspaceForDetail] = useState<WorkspaceResponse | null>(null)
  const [detailRefreshKey, setDetailRefreshKey] = useState(0)
  const [isSubmitting, setIsSubmitting] = useState(false)
  // 사이드바 메뉴 설정 편집 패널
  const [isMenuSettingsPanelOpen, setIsMenuSettingsPanelOpen] = useState(false)
  const [menuSettingsTargetWorkspace, setMenuSettingsTargetWorkspace] = useState<WorkspaceResponse | null>(null)
  
  // Redux state
  const { workspaces, isLoading } = useAppSelector(state => state.workspaceManagement)
  const selectedWorkspace = useAppSelector(state => state.workspace.selectedWorkspace)

  // Load workspaces when component mounts or workspace changes
  useEffect(() => {
    if (selectedWorkspace?.org_id) {
      dispatch(fetchWorkspaces({
        orgId: selectedWorkspace.org_id,
        page: 1,
        perPage: 100,
        includeUnavailable: true
      }))
    }
  }, [dispatch, selectedWorkspace?.org_id])

  // DetailPanel은 이제 자체 fetch + refreshKey로 갱신되므로 selectedWorkspaceForDetail
  // 참조를 workspaces 목록과 동기화할 필요가 없어졌다.
  // 다만 EditPanel/MenuSettingsEditPanel의 form 초기값으로 쓰이므로 click 시점에 한 번
  // 설정한 객체를 그대로 유지.

  // Clear error when component mounts
  useEffect(() => {
    dispatch(clearError())
  }, [dispatch])

  const handleCreateWorkspace = () => {
    setIsCreateModalOpen(true)
  }

  const handleCreateWorkspaceSubmit = async (workspaceData: { name: string; sortKey?: number }) => {
    // Prevent duplicate submissions
    if (isSubmitting) {
      return
    }

    if (!selectedWorkspace?.org_id) {
      console.error('No workspace selected')
      return
    }

    setIsSubmitting(true)
    try {
      await dispatch(createWorkspace({
        org_id: selectedWorkspace.org_id,
        name: workspaceData.name,
        sort_key: workspaceData.sortKey
      })).unwrap()
      
      // Close modal on success
      setIsCreateModalOpen(false)
      setIsSubmitting(false)
      showSuccess('워크스페이스가 생성되었습니다.')

      // Refresh workspace list
      dispatch(fetchWorkspaces({
        orgId: selectedWorkspace.org_id,
        page: 1,
        perPage: 100,
        includeUnavailable: true
      }))
    } catch (err: any) {
      console.error('Failed to create workspace:', err)
      handleApiError(err, showError, ErrorMessages.CREATE_FAILED)

      // Keep modal open on error so user can retry
    } finally {
      setIsSubmitting(false)
    }
  }


  const handleEditWorkspaceSubmit = async (workspaceData: { name: string; sortKey?: number; available: boolean; startDate?: string | null; endDate?: string | null; showTasks: boolean }) => {
    if (!editingWorkspace?.id || !selectedWorkspace?.org_id) {
      console.error('No workspace selected for editing or no org selected')
      return
    }

    try {
      await dispatch(updateWorkspace({
        workspaceId: editingWorkspace.id,
        data: {
          name: workspaceData.name,
          sort_key: workspaceData.sortKey,
          available: workspaceData.available,
          start_date: workspaceData.startDate,
          end_date: workspaceData.endDate,
          show_tasks: workspaceData.showTasks
        }
      })).unwrap()

      // 현재 선택된 워크스페이스라면 store 를 갱신해 현황 화면에 세부실행과제 표시 설정을 바로 반영
      dispatch(fetchWorkspaceById(editingWorkspace.id))

      // Close panel on success
      setIsEditPanelOpen(false)
      
      // Refresh workspace list
      const fetchResult = await dispatch(fetchWorkspaces({
        orgId: selectedWorkspace.org_id,
        page: 1,
        perPage: 100,
        includeUnavailable: true
      }))
      
      // Reopen detail panel with updated workspace data
      if (fetchResult.payload && Array.isArray(fetchResult.payload)) {
        const updatedWorkspace = fetchResult.payload.find((w: WorkspaceResponse) => w.id === editingWorkspace.id)
        if (updatedWorkspace) {
          setSelectedWorkspaceForDetail(updatedWorkspace)
          setIsDetailPanelOpen(true)
          // DetailPanel이 자체 fetch로 최신 데이터 재조회
          setDetailRefreshKey(k => k + 1)
        }
      }
      setEditingWorkspace(null)
    } catch (err: any) {
      console.error('Failed to update workspace:', err)
      handleApiError(err, showError, ErrorMessages.UPDATE_FAILED)

      // Keep panel open on error so user can retry
    }
  }

  const handleEditPanelClose = () => {
    setIsEditPanelOpen(false)
    setEditingWorkspace(null)
  }

  const handleRowClick = (workspaceId: string) => {
    const workspace = workspaces.find(w => w.id === workspaceId)
    if (workspace) {
      setSelectedWorkspaceForDetail(workspace)
      setIsDetailPanelOpen(true)
    }
  }

  const handleDetailPanelClose = () => {
    setIsDetailPanelOpen(false)
    setSelectedWorkspaceForDetail(null)
  }

  // AG-Grid column definitions
  const columnDefs: ColDef[] = useMemo(() => [
    {
      headerName: '순서',
      field: 'sort_key',
      width: 100
    },
    {
      headerName: '상태',
      field: 'available',
      width: 120,
      cellRenderer: (params: any) => (
        <div className="flex items-center h-full">
          <Tag 
            color={params.value ? "success" : "gray"} 
            size="small"
          >
            ● {params.value ? '활성' : '비활성'}
          </Tag>
        </div>
      )
    },
    {
      headerName: '이름',
      field: 'name',
      flex: 1,
      minWidth: 200
    },
    {
      headerName: '생성일시',
      field: 'created_at',
      width: 180,
      cellRenderer: (params: any) => {
        if (!params.value) return '-'
        return new Date(params.value).toLocaleString('ko-KR', {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit'
        })
      }
    }
  ], [])

  return (
    <div className="p-6 h-full flex flex-col">
      {/* Filter & Action Bar - Single Row */}
      <div className="py-2 px-4 flex-shrink-0 flex items-center gap-2 flex-wrap justify-end rounded-lg mb-6 bg-[#F6F6F7]">
        {/* Create Workspace Button */}
        <Button
          color="dark"
          size="medium"
          onClick={handleCreateWorkspace}
          className="h-10 px-[10px] flex items-center gap-2 flex-shrink-0 whitespace-nowrap"
        >
          <Plus className="w-4 h-4" strokeWidth={2} />
          워크스페이스 생성
        </Button>
      </div>


      {/* Workspace List Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 flex-1 min-h-0">
        {isLoading ? (
          <div className="flex items-center justify-center h-96">
            <p className="text-gray-500">데이터를 불러오는 중...</p>
          </div>
        ) : (
          <DataGrid
            columnDefs={columnDefs}
            rowData={workspaces}
            headerHeight={40}
            rowHeight={42}
            pagination={true}
            paginationPageSize={20}
            paginationPageSizeSelector={[10, 20, 50]}
            height="calc(100vh - 210px)"
            rowSelection="single"
            onRowClicked={(event) => handleRowClick(event.data.id)}
            getRowStyle={(params) => {
              // 비활성화된 워크스페이스의 경우 시각적으로 구분
              if (!params.data.available) {
                return {
                  opacity: 0.6,
                  backgroundColor: '#f9f9f9'
                }
              }
              return undefined
            }}
          />
        )}
      </div>

      {/* Workspace Create Modal */}
      <WorkspaceCreateModal
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onSubmit={handleCreateWorkspaceSubmit}
        isLoading={isSubmitting}
      />

      {/* Workspace Edit Panel */}
      <WorkspaceEditPanel
        isOpen={isEditPanelOpen}
        onClose={handleEditPanelClose}
        onSubmit={handleEditWorkspaceSubmit}
        workspace={editingWorkspace}
        isLoading={isLoading}
      />

      {/* Workspace Detail Panel — id만 전달, 패널이 자체 fetch */}
      <WorkspaceDetailPanel
        isOpen={isDetailPanelOpen}
        onClose={handleDetailPanelClose}
        workspaceId={selectedWorkspaceForDetail?.id ?? null}
        refreshKey={detailRefreshKey}
        onEdit={(workspace) => {
          setEditingWorkspace(workspace)
          setIsEditPanelOpen(true)
          setIsDetailPanelOpen(false) // Close detail panel when opening edit panel
        }}
        onEditMenuSettings={(workspace) => {
          setMenuSettingsTargetWorkspace(workspace)
          setIsMenuSettingsPanelOpen(true)
          // 상세 패널은 열린 상태 유지 — 저장 후 refreshKey로 자동 refetch
        }}
      />

      {/* Workspace Menu Settings Edit Panel */}
      <WorkspaceMenuSettingsEditPanel
        isOpen={isMenuSettingsPanelOpen}
        onClose={() => {
          setIsMenuSettingsPanelOpen(false)
          setMenuSettingsTargetWorkspace(null)
        }}
        workspaceId={menuSettingsTargetWorkspace?.id ?? ''}
        workspaceName={menuSettingsTargetWorkspace?.name ?? ''}
        initialMenuSettings={menuSettingsTargetWorkspace?.menu_settings}
        onSuccess={(items) => {
          // DetailPanel은 자체 fetch하므로 refreshKey만 올리면 최신 menu_settings 반영됨.
          // (Redux의 state.workspace.selectedWorkspace는 thunk extraReducer에서 처리 → 사이드바)
          setDetailRefreshKey(k => k + 1)
          // MenuSettingsEditPanel의 initialMenuSettings 재진입 대비 (사용자가 패널 다시 열 때 최신 보이도록)
          setMenuSettingsTargetWorkspace(prev =>
            prev ? { ...prev, menu_settings: items } : prev
          )
          // workspaceManagement 슬라이스의 워크스페이스 목록도 새로고침
          // (목록을 다시 클릭해 들어와도 최신 데이터로 보이도록)
          if (selectedWorkspace?.org_id) {
            dispatch(fetchWorkspaces({
              orgId: selectedWorkspace.org_id,
              page: 1,
              perPage: 100,
              includeUnavailable: true,
            }))
          }
        }}
      />
    </div>
  )
}